<template>
  <div class="market-management">
    <div class="toolbar">
      <el-button type="primary" @click="showAddDialog">添加资源</el-button>
    </div>

    <div class="filters">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索资源名称或描述..."
        class="search-input"
        clearable
        @input="handleSearch"
        style="width: 300px; margin-right: 15px;"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-select
        v-model="selectedCategory"
        placeholder="选择分类"
        clearable
        @change="handleFilterChange"
        style="width: 200px;"
      >
        <el-option
          v-for="cat in categories"
          :key="cat.id"
          :label="cat.name"
          :value="cat.id"
        />
      </el-select>
    </div>

    <el-table :data="displayItems" border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="名称">
        <template #default="{ row }">
          <span style="display: flex; align-items: center; gap: 8px;">
            <el-tag v-if="row.is_pinned" type="warning" size="small">置顶</el-tag>
            <span>{{ row.name }}</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="type" label="类型">
        <template #default="{ row }">
          <el-tag :type="getTypeColor(row.type)">{{ getTypeName(row.type) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="category.name" label="分类" />
      <el-table-column prop="file_name" label="文件" width="200">
        <template #default="{ row }">
          <span v-if="row.file_name">{{ row.file_name }}</span>
          <span v-else style="color: rgba(255, 255, 255, 0.5);">无文件</span>
        </template>
      </el-table-column>
      <el-table-column prop="download_count" label="下载" width="100" />
      <el-table-column prop="is_pinned" label="置顶" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_pinned"
            @change="togglePinned(row)"
            active-text="置顶"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="view_count" label="浏览" width="100" />
      <el-table-column prop="created_at" label="创建时间" width="180">
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column prop="is_active" label="启用" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_active"
            @change="toggleStatus(row)"
            active-text="启用"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button size="small" @click="editItem(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="deleteItem(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > 0"
      :current-page="currentPage"
      :page-sizes="[20, 50, 100, { value: -1, label: '全部' }]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="pagination"
    />

    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="类型">
          <el-select v-model="form.type">
            <el-option label="应用" value="app" />
            <el-option label="知识库" value="knowledge" />
            <el-option label="工具" value="tool" />
            <el-option label="MCP" value="mcp" />
          </el-select>
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="5" />
        </el-form-item>
        <el-form-item label="分类">
          <el-select v-model="form.category_id" clearable>
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-select v-model="form.tag_ids" multiple>
            <el-option v-for="tag in tags" :key="tag.id" :label="tag.name" :value="tag.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="版本要求">
          <el-input
            v-model="form.version_requirement"
            placeholder="例如：maxkb v2.3.1及以上、不限等"
            clearable
          />
        </el-form-item>
        <el-form-item label="资源文件">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :limit="1"
            :file-list="fileList"
          >
            <el-button type="primary">选择文件</el-button>
            <template #tip>
              <div class="el-upload__tip">支持任意文件格式，文件将在保存时上传</div>
            </template>
          </el-upload>
          <div v-if="form.file" class="file-info">
            <span>已选择: {{ form.file.name }}</span>
          </div>
          <div v-else-if="form.file_name && !form.file" class="file-info">
            <span>当前文件: {{ form.file_name }}</span>
            <el-button type="text" size="small" @click="previewFile" style="margin-left: 10px;">预览</el-button>
          </div>
        </el-form-item>
        <el-form-item label="置顶">
          <el-switch v-model="form.is_pinned" active-text="置顶" inactive-text="" />
          <div class="form-tip">置顶的资源会优先显示在列表前面</div>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch
            v-model="form.is_active"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api'
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'MarketManagement',
  components: {
    Search
  },
  data() {
    return {
      items: [],
      categories: [],
      tags: [],
      searchKeyword: '',
      selectedCategory: null,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      dialogVisible: false,
      dialogTitle: '添加资源',
      fileList: [], // el-upload 的文件列表
      form: {
        type: 'app',
        name: '',
        description: '',
        category_id: null,
        tag_ids: [],
        version_requirement: '',
        file: null,
        file_name: null,
        file_url: null,
        is_active: true
      },
      currentId: null
    }
  },
  computed: {
    displayItems() {
      return this.items
    }
  },
  mounted() {
    this.loadItems()
    this.loadCategories()
    this.loadTags()
  },
  methods: {
    async loadItems() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize === -1 ? 'all' : this.pageSize
        }
        
        // 添加分类筛选
        if (this.selectedCategory) {
          params.category = this.selectedCategory
        }
        
        // 添加搜索
        if (this.searchKeyword) {
          params.search = this.searchKeyword
        }
        
        const response = await api.get('/admin/market/items/', { params })
        
        if (Array.isArray(response.data)) {
          this.items = response.data
          this.total = response.data.length
        } else {
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
      } catch (error) {
        this.$message.error('加载失败')
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const response = await api.get('/admin/market/categories/')
        this.categories = Array.isArray(response.data) ? response.data : (response.data.results || [])
      } catch (error) {
        console.error('加载分类失败:', error)
        this.categories = []
      }
    },
    async loadTags() {
      try {
        const response = await api.get('/admin/market/tags/')
        this.tags = Array.isArray(response.data) ? response.data : (response.data.results || [])
      } catch (error) {
        console.error('加载标签失败:', error)
        this.tags = []
      }
    },
    showAddDialog() {
      this.dialogTitle = '添加资源'
      this.currentId = null
      this.form = {
        type: 'app',
        name: '',
        description: '',
        category_id: null,
        tag_ids: [],
        version_requirement: '',
        file: null,
        file_name: null,
        file_url: null,
        is_pinned: false,
        is_active: true
      }
      this.fileList = [] // 清空文件列表
      // 清空 el-upload 组件的文件列表
      this.$nextTick(() => {
        if (this.$refs.uploadRef) {
          this.$refs.uploadRef.clearFiles()
        }
      })
      this.dialogVisible = true
    },
    editItem(item) {
      this.dialogTitle = '编辑资源'
      this.currentId = item.id
      this.form = {
        type: item.type,
        name: item.name,
        description: item.description,
        category_id: item.category?.id || null,
        tag_ids: item.tags?.map(t => t.id) || [],
        version_requirement: item.version_requirement || '',
        file: null, // 编辑时清空新选择的文件
        file_name: item.file_name || null,
        file_url: item.file_url || null,
        is_pinned: item.is_pinned || false,
        is_active: item.is_active !== undefined ? item.is_active : true
      }
      this.fileList = [] // 清空文件列表
      // 清空 el-upload 组件的文件列表
      this.$nextTick(() => {
        if (this.$refs.uploadRef) {
          this.$refs.uploadRef.clearFiles()
        }
      })
      this.dialogVisible = true
    },
    async deleteItem(item) {
      try {
        await this.$confirm('确定要删除吗？', '提示', { type: 'warning' })
        await api.delete(`/admin/market/items/${item.id}/`)
        this.$message.success('删除成功')
        this.loadItems()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async saveItem() {
      try {
        const formData = new FormData()
        
        // 添加基本字段
        if (this.form.type) {
          formData.append('type', this.form.type)
        }
        if (this.form.name) {
          formData.append('name', this.form.name)
        }
        if (this.form.description) {
          formData.append('description', this.form.description)
        }
        if (this.form.category_id) {
          formData.append('category_id', this.form.category_id)
        }
        
        // 添加标签
        if (this.form.tag_ids && this.form.tag_ids.length > 0) {
          this.form.tag_ids.forEach(id => {
            formData.append('tag_ids', id)
          })
        }
        
        // 添加版本要求
        if (this.form.version_requirement) {
          formData.append('version_requirement', this.form.version_requirement)
        }
        
        // 添加文件
        if (this.form.file) {
          formData.append('file', this.form.file)
        }
        
        // 添加置顶和状态（FormData会自动转换布尔值，但为了确保，我们显式处理）
        formData.append('is_pinned', String(this.form.is_pinned))
        formData.append('is_active', String(this.form.is_active))

        if (this.currentId) {
          await api.patch(`/admin/market/items/${this.currentId}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.$message.success('更新成功')
        } else {
          await api.post('/admin/market/items/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.$message.success('添加成功')
        }
        this.dialogVisible = false
        // 清空表单和文件列表
        this.form.file = null
        this.fileList = []
        this.$nextTick(() => {
          if (this.$refs.uploadRef) {
            this.$refs.uploadRef.clearFiles()
          }
        })
        this.loadItems()
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败: ' + (error.response?.data?.detail || error.message))
      }
    },
    handleFileChange(file, fileList) {
      this.fileList = fileList
      if (fileList.length > 0) {
        this.form.file = file.raw
        // 如果选择了新文件，清空旧的文件信息
        this.form.file_name = null
        this.form.file_url = null
      } else {
        this.form.file = null
      }
    },
    handleFileRemove() {
      this.form.file = null
      this.fileList = []
      // 如果是在编辑模式下删除文件，保留原有的文件信息
      // 如果需要删除原有文件，可以在这里设置一个标志
    },
    previewFile() {
      if (this.form.file_url) {
        window.open(this.form.file_url, '_blank')
      }
    },
    async toggleStatus(item) {
      try {
        await api.patch(`/admin/market/items/${item.id}/`, {
          is_active: item.is_active
        })
        this.$message.success(item.is_active ? '已启用' : '已禁用')
      } catch (error) {
        // 恢复原状态
        item.is_active = !item.is_active
        this.$message.error('状态更新失败')
        console.error(error)
      }
    },
    async togglePinned(item) {
      try {
        await api.patch(`/admin/market/items/${item.id}/`, {
          is_pinned: item.is_pinned
        })
        this.$message.success(item.is_pinned ? '已置顶' : '已取消置顶')
        this.loadItems() // 重新加载以更新排序
      } catch (error) {
        // 恢复原状态
        item.is_pinned = !item.is_pinned
        this.$message.error('置顶状态更新失败')
        console.error(error)
      }
    },
    getTypeName(type) {
      const map = { app: '应用', knowledge: '知识库', tool: '工具', mcp: 'MCP' }
      return map[type] || type
    },
    getTypeColor(type) {
      const map = { app: 'success', knowledge: 'info', tool: 'warning', mcp: 'danger' }
      return map[type] || ''
    },
    handleSearch() {
      this.currentPage = 1
      this.loadItems()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.pageSize !== -1) {
        this.loadItems()
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.market-management {
  width: 100%;
}

.toolbar {
  margin-bottom: 20px;
}

.filters {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 5px;
}

.file-info {
  margin-top: 10px;
  padding: 8px;
  background-color: rgba(0, 150, 255, 0.1);
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 14px;
}
</style>

